import { Injectable, signal, computed } from '@angular/core';

/**
 * Program Context Interface
 *
 * Contains 4 separate key entities:
 * - id: Program ID (number) - numeric identifier
 * - uid: Program UID (string) - used in X-Program-Id header
 * - programToken: Device Token (string) - from program-login, used as Authorization
 * - name: Program name for display
 *
 * Note: Master Token is stored separately in localStorage['auth-token']
 */
export interface ProgramContext {
  id: number; // Program ID - numeric identifier (0 = new program)
  name: string; // Program name for display
  uid?: string; // Program UID - used in X-Program-Id header for schema routing
  programToken?: string; // Device Token - from program-login, used as Authorization header
  status?: 'ongoing' | 'completed' | 'on-hold';
  startDate?: string;
  endDate?: string;
  accountManager?: string;
  managerEmail?: string; // Account manager email from API (for matching)
  managerName?: string; // Account manager name from API (for matching)
  // Additional fields from get-programs API for form prefilling
  programCode?: string;
  applicationName?: string | null;
  mappingType?: number | null;
  salesType?: number | null;
}

const STORAGE_KEY = 'superadmin_program_context';

@Injectable({
  providedIn: 'root',
})
export class ProgramService {
  // Signal-based state
  private programContext = signal<ProgramContext | null>(null);

  // Computed values for the 4 key entities
  readonly currentProgram = computed(() => this.programContext());
  readonly programId = computed(() => this.programContext()?.id ?? null); // Program ID (number)
  readonly programName = computed(() => this.programContext()?.name ?? 'No Program Selected');
  readonly programUid = computed(() => this.programContext()?.uid ?? null); // Program UID (for X-Program-Id header)
  readonly programToken = computed(() => this.programContext()?.programToken ?? null); // Device Token (for Authorization)
  readonly isNewProgram = computed(() => this.programContext()?.id === 0);
  readonly hasProgram = computed(() => this.programContext() !== null);
  readonly hasProgramToken = computed(() => !!this.programContext()?.programToken);

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Set the active program context
   * @param program - Program details or just ID (0 for new program)
   */
  setProgram(program: ProgramContext | number): void {
    if (typeof program === 'number') {
      // Just ID provided - create minimal context
      this.programContext.set({
        id: 1,
        name: program === 0 ? 'New Program' : `Program #${program}`,
      });
    } else {
      this.programContext.set(program);
    }
    this.saveToStorage();
  }

  /**
   * Set program for "Create New" flow
   */
  setNewProgram(): void {
    this.programContext.set({
      id: 0,
      name: 'New Program',
      status: 'ongoing',
    });
    this.saveToStorage();
  }

  /**
   * Clear the program context
   */
  clearProgram(): void {
    this.programContext.set(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('selected_program'); // Also clear selected_program
  }

  /**
   * Update program details (after API response)
   */
  updateProgram(updates: Partial<ProgramContext>): void {
    const current = this.programContext();
    if (current) {
      this.programContext.set({ ...current, ...updates });
      this.saveToStorage();
    }
  }

  private saveToStorage(): void {
    const context = this.programContext();
    if (context) {
      // Save to primary storage key
      localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
      
      // Also save to 'selected_program' for compatibility with program-members
      // This ensures APIs that check 'selected_program' get the UID
      if (context.uid) {
        const selectedProgramData = {
          program_id: context.id,
          program_name: context.name,
          uid: context.uid,
          manager_name: context.accountManager,
          prog_start_date: context.startDate,
          prog_end_date: context.endDate,
          device_token: context.programToken,
        };
        localStorage.setItem('selected_program', JSON.stringify(selectedProgramData));
      }
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ProgramContext;
        this.programContext.set(parsed);
      }
    } catch (error) {
      console.warn('Failed to load program context from storage:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  // Legacy compatibility - remove after migration
  /** @deprecated Use programId() signal instead */
  get currentProgramId(): number | null {
    return this.programId();
  }
}
